import { useState, useEffect } from 'react';

interface Section {
  id: string;
  label: string;
  icon: string;
}

interface Props {
  sections: Section[];
}

const CourseSectionNav = ({ sections }: Props) => {
  const [activeId, setActiveId] = useState<string>('');

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY + 130;
      let current = sections[0]?.id ?? '';
      for (const section of sections) {
        const el = document.getElementById(section.id);
        if (el && el.getBoundingClientRect().top + window.scrollY <= scrollY) {
          current = section.id;
        }
      }
      setActiveId(current);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [sections]);

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      const offset = el.getBoundingClientRect().top + window.scrollY - 90;
      window.scrollTo({ top: offset, behavior: 'smooth' });
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden mb-4">
      <div className="px-4 pt-4 pb-2 border-b border-slate-100">
        <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1.5">
          <div className="w-3.5 h-3.5 flex items-center justify-center">
            <i className="ri-map-pin-2-line text-slate-300 text-xs" />
          </div>
          頁面導覽
        </h4>
      </div>
      <nav className="px-2 py-2">
        {sections.map((section, idx) => {
          const active = activeId === section.id;
          return (
            <button
              key={section.id}
              onClick={() => scrollTo(section.id)}
              className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-sm transition-all duration-200 cursor-pointer text-left group relative ${
                active
                  ? 'bg-violet-50 text-violet-600'
                  : 'text-slate-500 hover:bg-slate-50 hover:text-slate-800'
              }`}
            >
              {/* Step indicator line */}
              {idx < sections.length - 1 && (
                <span
                  className={`absolute left-[22px] top-8 w-px h-2 transition-colors duration-200 ${
                    active ? 'bg-violet-200' : 'bg-slate-150'
                  }`}
                  style={{ background: active ? '#ddd6fe' : '#e8eaed' }}
                />
              )}

              {/* Icon container */}
              <div
                className={`w-6 h-6 flex items-center justify-center rounded-lg flex-shrink-0 transition-all duration-200 ${
                  active
                    ? 'bg-violet-100'
                    : 'bg-slate-100 group-hover:bg-slate-200'
                }`}
              >
                <i
                  className={`${section.icon} text-xs transition-colors duration-200 ${
                    active ? 'text-violet-600' : 'text-slate-400 group-hover:text-slate-600'
                  }`}
                />
              </div>

              <span className={`flex-1 text-xs font-medium leading-tight ${active ? 'font-semibold' : ''}`}>
                {section.label}
              </span>

              {active && (
                <span className="w-1.5 h-1.5 bg-violet-500 rounded-full flex-shrink-0 animate-pulse" />
              )}
            </button>
          );
        })}
      </nav>
    </div>
  );
};

export default CourseSectionNav;
